import React from "react";
import {
  formatDuration,
  getDueAt,
  getOpenStartDate,
  getSlaHours,
  isClosedStatus,
} from "../../utils/uaucAllUtils";

function getRiskClass(value, severity) {
  const riskValue = Number(value);
  const riskSeverity = String(severity || "").toLowerCase();

  if (riskValue >= 4 || riskSeverity.includes("critical")) {
    return "bg-red-600 text-white ring-red-600";
  }

  if (riskValue >= 3 || riskSeverity.includes("high")) {
    return "bg-red-100 text-red-800 ring-red-200";
  }

  if (
    riskValue >= 2 ||
    riskSeverity.includes("medium") ||
    riskSeverity.includes("moderate")
  ) {
    return "bg-orange-100 text-orange-800 ring-orange-200";
  }

  if (riskValue >= 1 || riskSeverity.includes("low")) {
    return "bg-yellow-100 text-yellow-800 ring-yellow-200";
  }

  return "bg-slate-100 text-slate-700 ring-slate-200";
}

function getStatusClass(status) {
  if (isClosedStatus(status)) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  }

  return "bg-amber-50 text-amber-700 ring-amber-200";
}

function getSlaBadge(item) {
  const opened = getOpenStartDate(item);
  const dueAt = getDueAt(item);
  const closed = isClosedStatus(item?.status);

  if (!dueAt) {
    return <span className="text-slate-400">-</span>;
  }

  if (closed) {
    const closedAt =
      item?.closedOn || item?.closedAt || item?.resolvedAt || item?.updatedAt;

    if (!closedAt || !opened) {
      return <span className="text-slate-400">-</span>;
    }

    const closedTime = new Date(closedAt).getTime();
    const openedTime = new Date(opened).getTime();
    const late = closedTime > dueAt.getTime();
    const duration = closedTime - openedTime;

    return (
      <div className="flex flex-col gap-1">
        <span
          className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-black ring-1 ${
            late
              ? "bg-red-50 text-red-700 ring-red-200"
              : "bg-emerald-50 text-emerald-700 ring-emerald-200"
          }`}
        >
          {late ? "Closed Late" : "Closed On Time"}
        </span>

        <span className="text-[11px] font-bold text-slate-400">
          Took {formatDuration(duration)}
        </span>
      </div>
    );
  }

  const overdue = Date.now() > dueAt.getTime();
  const slaHours = getSlaHours(item);

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ring-1 ${
        overdue
          ? "bg-red-50 text-red-700 ring-red-200"
          : "bg-emerald-50 text-emerald-700 ring-emerald-200"
      }`}
    >
      {overdue ? "Overdue" : "On Time"} · {slaHours}h
    </span>
  );
}

export default function UAUCObservationTable({ items = [], onOpenEdit }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-y border-slate-200 bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">Opened</th>
            <th className="px-4 py-3">Open Age</th>
            <th className="px-4 py-3">SLA</th>
            <th className="px-4 py-3">Observer</th>
            <th className="px-4 py-3">Area</th>
            <th className="px-4 py-3">Risk</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 bg-white">
          {items.map((item, index) => {
            const opened = getOpenStartDate(item);
            const openedDate = opened ? new Date(opened) : null;
            const closed = isClosedStatus(item?.status);

            const riskValue = item?.riskValue?.value || "";
            const riskSeverity = item?.riskValue?.severity || "";
            const riskLabel =
              riskValue && riskSeverity
                ? `${riskValue} (${riskSeverity})`
                : riskValue || riskSeverity || "-";

            return (
              <tr
                key={item?._id || index}
                onClick={() => onOpenEdit?.(item)}
                className={`cursor-pointer transition hover:bg-sky-50 ${
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                }`}
              >
                <td className="whitespace-nowrap px-4 py-3 font-bold text-slate-800">
                  {openedDate ? openedDate.toLocaleDateString() : "-"}
                </td>

                <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-600">
                  {opened && !closed
                    ? formatDuration(Date.now() - new Date(opened).getTime())
                    : "-"}
                </td>

                <td className="whitespace-nowrap px-4 py-3">
                  {getSlaBadge(item)}
                </td>

                <td className="px-4 py-3 font-semibold text-slate-700">
                  {item?.observer?.name || "-"}
                </td>

                <td className="px-4 py-3 font-semibold text-slate-700">
                  {item?.area || "-"}
                </td>

                <td className="whitespace-nowrap px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ring-1 ${getRiskClass(
                      riskValue,
                      riskSeverity,
                    )}`}
                  >
                    {riskLabel}
                  </span>
                </td>

                <td className="whitespace-nowrap px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-black ring-1 ${getStatusClass(
                      item?.status,
                    )}`}
                  >
                    {item?.status || "-"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
